import { ChannelEnum, ProductAssociableDto } from '@c2b/muven-commons'

// O commons não exporta a classe da variação
type ProductAssociableVariationDto = ProductAssociableDto['variations'][number]

export function isNotFoundError(err: any): boolean {
    const messageNotFound = !!err && !!err.message && (
        err.message.indexOf('404') > -1 ||
        err.message.toLowerCase().indexOf('not found') > -1 ||
        err.message.toLowerCase().indexOf('not_found') > -1
    );
    const statusNotFound = !!err && err.status === '404';
    return messageNotFound || statusNotFound;
}

/**
 * Procura a variação com o idExternalChannel informado no produto retornado pelo canal.
 * Quando o canal devolve o próprio SKU sem variações, o produto é a variação.
 */
export function findChannelVariation(productInChannel: any, idExternalChannel: any): any {
    if (!productInChannel) {
        return null
    }

    const variation = (productInChannel.variations || []).find((v: any) => String(v.idExternalChannel) === String(idExternalChannel))
    if (variation) {
        return variation
    }

    if (!productInChannel.hasVariations() && String(productInChannel.idExternalChannel) === String(idExternalChannel)) {
        return productInChannel
    }

    return null
}

// Canais em que um produto simples no Muven associado a uma variação do canal
// grava `item_id+model_id` (mesma regra do linkProducts da tela de associação).
const PARENT_PLUS_CHILD_CHANNELS = [
    ChannelEnum.MercadoLivre,
    ChannelEnum.WooCommerce,
    ChannelEnum.Shopee,
    ChannelEnum.NuvemShop,
]

/**
 * Id que deve ser gravado quando um produto simples do Muven é associado a uma
 * variação do canal. Na Tray o id da variação ganha o sufixo `_V`, porque lá o
 * produto pai e a variação vêm de tabelas diferentes e podem ter o mesmo id.
 */
export function buildSimpleToVariationId(enumChannel: ChannelEnum, idParent: string, idVariation: string): string {
    if (PARENT_PLUS_CHILD_CHANNELS.includes(enumChannel)) {
        return `${idParent}+${idVariation}`
    }

    if (enumChannel === ChannelEnum.Tray) {
        return `${idVariation}_V`
    }

    return String(idVariation)
}

export enum ChannelProductMatchStatus {
    // O id aponta para um produto simples do canal ou para uma variação dele
    Found = 'found',
    // O id aponta para um anúncio/produto pai com variações: um produto simples
    // do Muven precisa ser associado a uma das variações
    ParentWithVariations = 'parentWithVariations',
    NotFound = 'notFound',
}

export interface ChannelProductMatch {
    status: ChannelProductMatchStatus
    name?: string
    skuCode?: string
    isActive?: boolean
    // O id resolveu numa variação (filho) e não no produto pai
    isVariation?: boolean
    idParent?: string
    idVariation?: string
    attributes?: Array<{name: string, value: string}>
    variations?: ProductAssociableVariationDto[]
}

function splitId(idExternalChannel: string): {idParent?: string, idVariation?: string, pointsToVariation: boolean} {
    const id = String(idExternalChannel || '').trim()

    if (id.indexOf('+') > -1) {
        const [idParent, idVariation] = id.split('+')
        return {idParent, idVariation, pointsToVariation: true}
    }

    if (/_V$/.test(id)) {
        return {idVariation: id.replace(/_V$/, ''), pointsToVariation: true}
    }

    return {idVariation: id, pointsToVariation: false}
}

/**
 * Texto de um atributo que o agente pode ter mandado como objeto ({nome}, {valor}...).
 * Nunca devolve "[object Object]": o que não for texto vira vazio.
 */
export function attributeText(value: any): string {
    if (value === null || value === undefined) {
        return ''
    }

    if (typeof value === 'object') {
        return attributeText(value.nome ?? value.valor ?? value.name ?? value.value)
    }

    return typeof value === 'string' || typeof value === 'number' ? String(value).trim() : ''
}

function getGridAttributes(variation: ProductAssociableVariationDto): Array<{name: string, value: string}> {
    return ([variation.getFirstGridInfo(), variation.getSecondGridInfo()].filter((g) => !!g) as Array<{name: string, value: any}>)
        .map((g) => ({name: attributeText(g.name), value: attributeText(g.value)}))
        .filter((g) => !!g.value)
}

/**
 * Interpreta o retorno do "get" por id no canal para o id associado a um produto
 * simples do Muven. Os agentes, quando recebem `pai+filho` ou `filho_V`, devolvem
 * o pai com as variações já filtradas para a do id.
 */
export function resolveChannelProduct(productInChannel: ProductAssociableDto | null | undefined, idExternalChannel: string): ChannelProductMatch {
    if (!productInChannel || (!productInChannel.name && !productInChannel.idExternalChannel)) {
        return {status: ChannelProductMatchStatus.NotFound}
    }

    const {idVariation, pointsToVariation} = splitId(idExternalChannel)
    const base = {
        name: productInChannel.name,
        isActive: productInChannel.isActive,
        idParent: productInChannel.idExternalChannel,
    }

    if (!productInChannel.hasVariations()) {
        // Pediu uma variação e o canal não devolveu nenhuma: o filho não existe
        if (pointsToVariation) {
            return {status: ChannelProductMatchStatus.NotFound}
        }

        return {...base, status: ChannelProductMatchStatus.Found, skuCode: productInChannel.skuCode, isVariation: false}
    }

    // Alguns agentes (WBuy) já devolvem a variação com o id completo, pai+filho
    let variation = productInChannel.variations.find((v) => String(v.idExternalChannel) === String(idVariation)
        || String(v.idExternalChannel) === String(idExternalChannel).trim())

    if (!variation && pointsToVariation && productInChannel.variations.length === 1) {
        variation = productInChannel.variations[0]
    }

    if (variation) {
        return {
            ...base,
            status: ChannelProductMatchStatus.Found,
            skuCode: variation.skuCode || productInChannel.skuCode,
            isActive: variation.isActive === undefined ? productInChannel.isActive : variation.isActive,
            isVariation: true,
            idVariation: String(variation.idExternalChannel),
            attributes: getGridAttributes(variation),
        }
    }

    if (pointsToVariation) {
        return {status: ChannelProductMatchStatus.NotFound}
    }

    return {
        ...base,
        status: ChannelProductMatchStatus.ParentWithVariations,
        skuCode: productInChannel.skuCode,
        isVariation: false,
        variations: productInChannel.variations,
    }
}

export function describeVariation(variation: ProductAssociableVariationDto): string {
    const attrs = getGridAttributes(variation)

    if (!attrs.length) {
        return variation.skuCode || String(variation.idExternalChannel)
    }

    return attrs.map((a) => `${a.name}: ${a.value}`).join(' · ')
}

export type MuvenSearchField = 'codeSource' | 'skuCode' | 'name'

/**
 * Como cada canal encontra produtos na associação. Levantado no código de cada
 * agente (GET /products?info=), não na documentação dos canais:
 * - `hint`: o que o campo de busca aceita, como o cliente entende.
 * - `codeLabel`: como o canal chama o código que ele recebe do Muven.
 * - `autoSearch`: campos do Muven usados na busca automática, na ordem de
 *   tentativa. É o mesmo campo que a exportação grava no canal.
 * - `exactCodeOnly`: o agente devolve qualquer coisa quando não acha; só vale
 *   resultado com o código exatamente igual.
 * - `searchErrorMeansEmpty`: o agente quebra (em vez de devolver lista vazia) quando não acha.
 */
export interface ChannelSearchProfile {
    hint: string
    codeLabel: string
    autoSearch: MuvenSearchField[]
    supportsSearch: boolean
    supportsGetById: boolean
    exactCodeOnly?: boolean
    searchErrorMeansEmpty?: boolean
    manualGuide?: string
}

const CODE_SOURCE_FIRST: MuvenSearchField[] = ['codeSource', 'skuCode']
const SKU_FIRST: MuvenSearchField[] = ['skuCode', 'codeSource']

function profile(p: Partial<ChannelSearchProfile>): ChannelSearchProfile {
    return {
        hint: 'Código SKU',
        codeLabel: 'SKU',
        autoSearch: CODE_SOURCE_FIRST,
        supportsSearch: true,
        supportsGetById: true,
        ...p,
    }
}

// Sem agente que atenda a busca/conferência: o proxy recusa ou o agente não tem a rota
const UNSUPPORTED = profile({hint: '', codeLabel: '', autoSearch: [], supportsSearch: false, supportsGetById: false})

const SEARCH_PROFILES: Partial<Record<ChannelEnum, ChannelSearchProfile>> = {
    // Busca só pelo nome (search_item); só dígitos também acha pelo item_id. Não busca SKU.
    [ChannelEnum.Shopee]: profile({hint: 'Nome do produto ou item_id', codeLabel: 'item_id', autoSearch: ['name']}),
    // Referência exata (produto e, se não achar, variação). Exporta reference = codeSource || skuCode.
    [ChannelEnum.Tray]: profile({hint: 'Código de referência', codeLabel: 'Código de referência'}),
    [ChannelEnum.TrayFbits]: UNSUPPORTED,
    // seller_sku, depois sku e, por último, o título: o próprio agente cai no nome.
    [ChannelEnum.MercadoLivre]: profile({hint: 'Código SKU ou nome do produto', autoSearch: ['skuCode', 'name']}),
    // Busca livre (q): nome e, pela API da Nuvemshop, SKU.
    [ChannelEnum.NuvemShop]: profile({hint: 'Nome ou SKU do produto', autoSearch: ['codeSource', 'skuCode', 'name']}),
    // Título OU SKU, por parte do texto.
    [ChannelEnum.Shopify]: profile({hint: 'Nome ou SKU do produto', autoSearch: ['codeSource', 'skuCode', 'name']}),
    // Só o SPU, criado pela Shein: nenhum campo do Muven tem esse código.
    [ChannelEnum.Shein]: profile({
        hint: 'Código SPU da Shein',
        codeLabel: 'SPU',
        autoSearch: [],
        manualGuide: 'A Shein só encontra o produto pelo código SPU, que é criado por ela. Copie o SPU no painel de vendedor da Shein e cole aqui.',
    }),
    // seller_sku exato; sem resultado, o agente devolve 20 produtos quaisquer. Não consulta por id.
    [ChannelEnum.TiktokShop]: profile({hint: 'Seller SKU', codeLabel: 'Seller SKU', autoSearch: SKU_FIRST, exactCodeOnly: true, supportsGetById: false}),
    // IdSku exato. Exporta IdSku = skuCode || codeSource.
    [ChannelEnum.MagazineLuiza]: profile({hint: 'Código SKU (IdSku)', codeLabel: 'IdSku', autoSearch: SKU_FIRST}),
    // SKU exato, do produto ou da variação. A variação recebe o skuCode.
    [ChannelEnum.B2W]: profile({autoSearch: SKU_FIRST}),
    // RefId do SKU, SkuId numérico ou RefId do produto. Exporta RefId = codeSource || skuCode.
    [ChannelEnum.VTex]: profile({hint: 'Código SKU (RefId)', codeLabel: 'RefId'}),
    [ChannelEnum.WooCommerce]: profile({}),
    // Código de estoque e depois código do produto. Quando não acha, o agente responde com erro.
    [ChannelEnum.WBuy]: profile({hint: 'Código do produto ou de estoque', codeLabel: 'Código', searchErrorMeansEmpty: true}),
    // Só SKU exato (não busca por nome).
    [ChannelEnum.LojaIntegrada]: profile({}),
    // SKU exato. Exporta sku = skuCode.
    [ChannelEnum.Magento_1_9]: profile({autoSearch: SKU_FIRST}),
    [ChannelEnum.Magento_2_3]: UNSUPPORTED,
    [ChannelEnum.Flexy]: UNSUPPORTED,
    [ChannelEnum.Azata]: UNSUPPORTED,
    [ChannelEnum.Custom]: UNSUPPORTED,
}

export function getChannelSearchProfile(enumChannel: ChannelEnum): ChannelSearchProfile {
    return SEARCH_PROFILES[enumChannel] || profile({})
}

/** O que o campo de busca da associação aceita no canal (o mesmo texto em todas as telas). */
export function getChannelSearchHint(enumChannel: ChannelEnum): string {
    return getChannelSearchProfile(enumChannel).hint
}

export interface ChannelSearchSuggestion {
    field: MuvenSearchField
    label: string
    term: string
}

export interface MuvenProductCodes {
    name?: string
    skuCode?: string
    codeSource?: string
}

/**
 * Termos da busca automática ao abrir a associação, na ordem de tentativa. Só
 * entra o que o produto tem preenchido e sem repetir o mesmo texto.
 */
export function getSuggestedSearches(enumChannel: ChannelEnum, product: MuvenProductCodes): ChannelSearchSuggestion[] {
    const {autoSearch, codeLabel} = getChannelSearchProfile(enumChannel)
    const labels: Record<MuvenSearchField, string> = {
        name: 'Nome do produto',
        skuCode: `${codeLabel} (SKU do Muven)`,
        codeSource: `${codeLabel} (código da retaguarda)`,
    }
    const seen = new Set<string>()

    return autoSearch
        .map((field) => ({field, label: labels[field], term: String(product[field] || '').trim()}))
        .filter((s) => {
            const key = s.term.toLowerCase()

            if (!s.term || seen.has(key)) {
                return false
            }

            seen.add(key)
            return true
        })
}

export interface ChannelAssociationOption {
    key: string
    // Exatamente o que será gravado na associação
    idToSave: string
    idParent?: string
    idVariation?: string
    isVariation: boolean
    attributes: Array<{name: string, value: string}>
    skuCode?: string
    eanCode?: string
    isActive?: boolean
    skuMatches: boolean
    isCurrent: boolean
}

export interface ChannelAssociationGroup {
    key: string
    name: string
    idExternalChannel: string
    isActive?: boolean
    hasVariations: boolean
    options: ChannelAssociationOption[]
}

function sameCode(a: string | undefined, codes: string[]): boolean {
    const code = String(a || '').trim().toLowerCase()

    return !!code && codes.some((c) => String(c || '').trim().toLowerCase() === code)
}

/**
 * Transforma o retorno da busca no canal em opções que podem ser escolhidas para
 * um produto simples do Muven: o próprio produto quando ele não tem variações,
 * ou cada variação (nunca o pai com variações). O id a gravar já sai montado.
 * O que tem o mesmo código do Muven (SKU ou código da retaguarda) vem primeiro, como sugestão.
 */
export function buildAssociationGroups(products: ProductAssociableDto[], enumChannel: ChannelEnum, muvenCodes: string | string[], currentId: string): ChannelAssociationGroup[] {
    const codes = (Array.isArray(muvenCodes) ? muvenCodes : [muvenCodes]).filter((c) => !!c)

    const groups = (products || []).map((product, index) => {
        const parentId = String(product.idExternalChannel || '')
        let options: ChannelAssociationOption[]

        if (!product.hasVariations()) {
            options = [{
                key: `p-${parentId}`,
                idToSave: parentId,
                isVariation: false,
                attributes: [],
                skuCode: product.skuCode,
                eanCode: product.eanCode,
                isActive: product.isActive,
                skuMatches: sameCode(product.skuCode, codes),
                isCurrent: parentId === String(currentId || ''),
            }]
        } else {
            options = product.variations.map((variation) => {
                const idVariation = String(variation.idExternalChannel || '')
                const idToSave = buildSimpleToVariationId(enumChannel, parentId, idVariation)

                return {
                    key: `v-${parentId}-${idVariation}`,
                    idToSave,
                    idParent: parentId,
                    idVariation,
                    isVariation: true,
                    attributes: getGridAttributes(variation),
                    skuCode: variation.skuCode,
                    eanCode: variation.eanCode,
                    isActive: variation.isActive,
                    skuMatches: sameCode(variation.skuCode, codes),
                    isCurrent: idToSave === String(currentId || ''),
                }
            })

            options.sort((a, b) => Number(b.skuMatches) - Number(a.skuMatches))
        }

        return {
            key: `g-${parentId}-${index}`,
            name: product.name,
            idExternalChannel: parentId,
            isActive: product.isActive,
            hasVariations: product.hasVariations(),
            options,
        }
    })

    const rank = (g: ChannelAssociationGroup) => g.options.some((o) => o.skuMatches) ? 0 : 1

    return groups.sort((a, b) => rank(a) - rank(b))
}

export interface ComposedIdPart {
    label: string
    value: string
}

/**
 * Explica, em partes legíveis, o id composto que será gravado. Vazio quando o
 * id é simples e não precisa de explicação.
 */
export function describeComposedId(enumChannel: ChannelEnum, option: ChannelAssociationOption): ComposedIdPart[] {
    if (!option.isVariation) {
        return []
    }

    const parentLabel = enumChannel === ChannelEnum.Shopee ? 'Anúncio' : 'Produto pai'

    if (PARENT_PLUS_CHILD_CHANNELS.includes(enumChannel)) {
        return [
            {label: parentLabel, value: option.idParent || ''},
            {label: 'Variação', value: option.idVariation || ''},
        ]
    }

    if (enumChannel === ChannelEnum.Tray) {
        return [
            {label: 'Variação', value: option.idVariation || ''},
            {label: 'Sufixo', value: '_V'},
        ]
    }

    return []
}

/**
 * Só as opções cujo código bate com o buscado, para canais cujo agente devolve
 * qualquer produto quando não acha (ver `exactCodeOnly`).
 */
export function keepExactMatches(groups: ChannelAssociationGroup[]): ChannelAssociationGroup[] {
    return groups
        .map((group) => ({...group, options: group.options.filter((o) => o.skuMatches)}))
        .filter((group) => group.options.length > 0)
}

// ---------------------------------------------------------------------------
// Grade: pai do Muven com o pai do canal e cada variação com uma variação
// (mesma regra do linkProducts da tela de associação: ids puros, sem + nem _V)
// ---------------------------------------------------------------------------

export interface MuvenGridVariation {
    idMuven: string | number
    values: string[]
    skuCode?: string
    codeSource?: string
    // Id gravado hoje para esta variação no canal
    currentId?: string | null
}

export type GridPairReason = 'code' | 'attributes' | 'current' | 'none'

export interface GridPair {
    idMuven: string | number
    idChannelVariation: string | null
    reason: GridPairReason
}

function normalizeText(value: any): string {
    return String(value || '').normalize('NFD').replace(/[̀-ͯ]/g, '').trim().toLowerCase()
}

function sameValues(a: string[], b: string[]): boolean {
    const left = a.map(normalizeText).filter((v) => !!v).sort()
    const right = b.map(normalizeText).filter((v) => !!v).sort()

    return left.length > 0 && left.length === right.length && left.every((v, i) => v === right[i])
}

/** Valores que identificam a variação no canal (atributos de grade; sem eles, todos os atributos). */
export function getChannelVariationValues(variation: ProductAssociableVariationDto): string[] {
    const grid = getGridAttributes(variation)
    const attrs = grid.length ? grid : (variation.attributes || [])

    return attrs.map((a) => attributeText(a.value)).filter((v) => !!v)
}

/**
 * Sugere o par de cada variação do Muven entre as variações do produto do canal,
 * sem repetir a mesma variação do canal: primeiro código igual (SKU ou código da
 * retaguarda), depois os mesmos valores de grade, depois o id associado hoje.
 */
export function suggestGridPairs(muvenVariations: MuvenGridVariation[], channelProduct: ProductAssociableDto): GridPair[] {
    const channelVariations = (channelProduct && channelProduct.variations) || []
    const used = new Set<string>()
    const pairs = new Map<string | number, GridPair>()

    const pick = (reason: GridPairReason, matches: (mv: MuvenGridVariation, cv: ProductAssociableVariationDto) => boolean) => {
        for (const mv of muvenVariations) {
            if (pairs.has(mv.idMuven)) {
                continue
            }

            const found = channelVariations.find((cv) => !used.has(String(cv.idExternalChannel)) && matches(mv, cv))

            if (found) {
                used.add(String(found.idExternalChannel))
                pairs.set(mv.idMuven, {idMuven: mv.idMuven, idChannelVariation: String(found.idExternalChannel), reason})
            }
        }
    }

    pick('code', (mv, cv) => sameCode(cv.skuCode, [mv.skuCode || '', mv.codeSource || ''].filter((c) => !!c)))
    pick('attributes', (mv, cv) => sameValues(mv.values, getChannelVariationValues(cv)))
    pick('current', (mv, cv) => !!mv.currentId && String(cv.idExternalChannel) === String(mv.currentId))

    return muvenVariations.map((mv) => pairs.get(mv.idMuven) || {idMuven: mv.idMuven, idChannelVariation: null, reason: 'none' as GridPairReason})
}

/** Variações do canal escolhidas para mais de uma variação do Muven. */
export function findDuplicatePairs(pairs: GridPair[]): Set<string> {
    const seen = new Set<string>()
    const duplicated = new Set<string>()

    pairs.forEach((p) => {
        if (!p.idChannelVariation) {
            return
        }

        if (seen.has(p.idChannelVariation)) {
            duplicated.add(p.idChannelVariation)
        }

        seen.add(p.idChannelVariation)
    })

    return duplicated
}

function sameId(a?: string | null, b?: string | null): boolean {
    return String(a || '') === String(b || '')
}

/**
 * Lista para gravar a associação da grade: o pai sempre (a associação das
 * variações depende dele) e só as variações que mudaram. Sem par = remove.
 */
export function buildGridAssociationPayload(
    idMuvenParent: string | number,
    idChannelParent: string,
    pairs: GridPair[],
    muvenVariations: MuvenGridVariation[],
): Array<{idMuven: string | number, idExternalChannel: string | null}> {
    const currentById = new Map(muvenVariations.map((mv) => [String(mv.idMuven), mv.currentId || null]))

    return [
        {idMuven: idMuvenParent, idExternalChannel: idChannelParent},
        ...pairs
            .filter((p) => !sameId(p.idChannelVariation, currentById.get(String(p.idMuven))))
            .map((p) => ({idMuven: p.idMuven, idExternalChannel: p.idChannelVariation || null})),
    ]
}

/** Remove a associação do pai e de todas as variações com o canal. */
export function buildGridRemovalPayload(idMuvenParent: string | number, muvenVariations: MuvenGridVariation[]) {
    return [
        {idMuven: idMuvenParent, idExternalChannel: null},
        ...muvenVariations.map((mv) => ({idMuven: mv.idMuven, idExternalChannel: null})),
    ]
}

export enum GridParentCheck {
    // O id do pai aponta para um produto com variações no canal
    Ok = 'gridOk',
    // Aponta para um produto sem variações: a grade não tem como casar
    Simple = 'gridSimple',
    // Aponta para uma variação, não para o produto pai
    Variation = 'gridVariation',
    NotFound = 'notFound',
}

/** Interpreta a conferência do id do pai de uma grade (no simples, o "produto pai" é que seria o erro). */
export function resolveGridParent(productInChannel: ProductAssociableDto | null | undefined, idExternalChannel: string): GridParentCheck {
    const match = resolveChannelProduct(productInChannel, idExternalChannel)

    if (match.status === ChannelProductMatchStatus.NotFound) {
        return GridParentCheck.NotFound
    }

    if (match.status === ChannelProductMatchStatus.ParentWithVariations) {
        return GridParentCheck.Ok
    }

    return match.isVariation ? GridParentCheck.Variation : GridParentCheck.Simple
}
